import type { FunctionComponent, ReactNode } from 'react';
import Markdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '~/ui/core/button/Button';
import { IconArrowExpandHor, IconMinimize45 } from '~/ui/core/icons';

import { remarkStreamingCaret } from './markdown-streaming-caret';
import { useChatStore } from './stores/chat-open-store';

type ChatMarkdownProps = {
    readonly children: string;
    /** Component overrides merged over the defaults (e.g. a `span` renderer for inline mentions). */
    readonly components?: Components;
    /** Extra remark plugins appended after `remark-gfm` (e.g. `remarkMentions`). */
    readonly remarkPlugins?: Options['remarkPlugins'];
    /** Shows the streaming caret at the end of the last line. */
    readonly streaming?: boolean;
};

const MarkdownTable: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => {
    const isMarkdownTableWide = useChatStore((state) => state.isMarkdownTableWide);
    const setMarkdownTableWide = useChatStore((state) => state.setMarkdownTableWide);

    return (
        <div className="my-xxs flex w-full max-w-full min-w-0 flex-col gap-xxs">
            <div className="flex justify-end" data-testid="chat-markdown-table-toolbar">
                <Button
                    aria-label={isMarkdownTableWide ? 'Collapse table' : 'Expand table'}
                    className="shrink-0"
                    onClick={() => setMarkdownTableWide(!isMarkdownTableWide)}
                    size="sm"
                    type="button"
                    variant="secondary"
                >
                    {isMarkdownTableWide ? (
                        <IconMinimize45 size={12} aria-hidden={true} />
                    ) : (
                        <IconArrowExpandHor size={12} aria-hidden={true} />
                    )}
                    {isMarkdownTableWide ? 'Collapse' : 'Expand'}
                </Button>
            </div>
            <div
                className="w-full max-w-full min-w-0 overflow-x-auto rounded-md border border-border-soft"
                data-testid="chat-markdown-table-scroll"
            >
                <table className="w-full border-collapse text-left text-[13px] leading-[18px]">{children}</table>
            </div>
        </div>
    );
};

const components: Components = {
    a: ({ children }) => children,
    blockquote: ({ children }) => (
        <blockquote className="my-xxs border-l-2 border-border-subtle pl-s text-text-content-subtle">
            {children}
        </blockquote>
    ),
    code: ({ children }) => (
        <code className="rounded-sm bg-surface-canvas px-xs py-[1px] font-mono text-[12px] text-text-primary">
            {children}
        </code>
    ),
    del: ({ children }) => <del className="text-text-content-subtle">{children}</del>,
    h1: ({ children }) => <h1 className="mb-xxs mt-xs text-body-s font-semibold text-text-primary">{children}</h1>,
    h2: ({ children }) => <h2 className="mb-xxs mt-xs text-body-s font-semibold text-text-primary">{children}</h2>,
    h3: ({ children }) => <h3 className="mb-xxs mt-xs text-body-xs font-semibold text-text-primary">{children}</h3>,
    input: (props) => <input {...props} className="mr-xs align-[-1px]" readOnly={true} type="checkbox" />,
    li: ({ children }) => <li className="pl-0">{children}</li>,
    ol: ({ children }) => <ol className="my-xxs list-decimal space-y-xxs">{children}</ol>,
    p: ({ children }) => <p className="my-0">{children}</p>,
    pre: ({ children }) => (
        <pre className="my-xxs overflow-x-auto rounded-sm bg-surface-canvas p-s text-[12px] leading-5 text-text-primary">
            {children}
        </pre>
    ),
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
    td: ({ children }) => (
        <td className="max-w-[18rem] whitespace-normal wrap-anywhere border border-border-soft px-s py-xs align-top">
            {children}
        </td>
    ),
    th: ({ children }) => (
        <th className="max-w-[18rem] whitespace-normal wrap-anywhere border border-border-soft bg-surface-softest px-s py-xs text-left font-semibold align-top">
            {children}
        </th>
    ),
    ul: ({ children }) => <ul className="my-xxs space-y-xxs">{children}</ul>,
};

export const ChatMarkdown: FunctionComponent<ChatMarkdownProps> = ({
    children,
    components: componentOverrides,
    remarkPlugins,
    streaming = false,
}) => (
    <div className="whitespace-normal" data-testid="chat-markdown">
        <Markdown
            components={{ ...components, ...componentOverrides }}
            remarkPlugins={[remarkGfm, ...(remarkPlugins ?? []), ...(streaming ? [remarkStreamingCaret] : [])]}
            skipHtml={true}
        >
            {children}
        </Markdown>
    </div>
);
